/**
 * useCategoryData — data-fetching hook for CategoryPage.
 *
 * Encapsulates all the effects that load category data (streams, stores,
 * trending, for-you, rentals, saved, products, product types) so the
 * page component itself is left with rendering only.
 *
 * Returns a single object with `state` (all reactive values), setters
 * the parent needs to expose to sub-components (filters/type/sort), and
 * derived values (savedInCategory, catIsRentable, cat).
 */
import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { SORT_OPTS } from "./CategoryRails";


export function useCategoryData({ id, user }) {
  const [cats, setCats] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [activeType, setActiveType] = useState("");
  const [products, setProducts] = useState([]);
  const [stores, setStores] = useState([]);
  const [streams, setStreams] = useState([]);
  const [trending, setTrending] = useState([]);
  const [forYou, setForYou] = useState([]);
  const [isPersonalized, setIsPersonalized] = useState(false);
  const [rentals, setRentals] = useState([]);
  const [filters, setFilters] = useState({});
  const [newOnly, setNewOnly] = useState(false);

  // Page-level sort — persisted per-session so the user doesn't
  // re-pick it on every category.
  const [sortId, setSortId] = useState(() => {
    try { return localStorage.getItem("cat_sort") || "rating"; } catch { return "rating"; }
  });
  useEffect(() => {
    try { localStorage.setItem("cat_sort", sortId); } catch { /* noop */ }
  }, [sortId]);
  const sort = SORT_OPTS.find(s => s.id === sortId) || SORT_OPTS[0];

  // Saved for the buyer (all categories) — used for the badge count.
  const [savedAll, setSavedAll] = useState([]);
  const [savedLoaded, setSavedLoaded] = useState(false);

  // Preferences data (lazy — only fetched when Prefs tab is opened).
  const [profile, setProfile] = useState(null);
  const [prefSchema, setPrefSchema] = useState(null);
  const [prefMsg, setPrefMsg] = useState(null);

  // Clear filters + chosen type when switching category — stale
  // size/gender/type should not leak across very different categories.
  useEffect(() => { setFilters({}); setActiveType(""); }, [id]);

  // Static per-page fetches: category list (for the switcher pill rail)
  useEffect(() => {
    api.get("/categories").then(({ data }) => setCats(data)).catch(() => setCats([]));
  }, []);

  // Product types for the current category — drives the chip rail
  useEffect(() => {
    if (!id) return;
    let ignore = false;
    api.get(`/product-types?category_id=${id}`)
      .then(({ data }) => { if (!ignore) setProductTypes(data || []); })
      .catch(() => { if (!ignore) setProductTypes([]); });
    return () => { ignore = true; };
  }, [id]);

  // Section data — live streams, trending, stores (all honour sort + type)
  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const streamParams = new URLSearchParams({ category_id: id, live_only: "true" });
        if (sort.streams) streamParams.set("sort", sort.streams);
        const storeParams = new URLSearchParams({ category_id: id });
        if (sort.stores) storeParams.set("sort", sort.stores);
        const trendParams = new URLSearchParams({ category_id: id });
        if (sort.products) trendParams.set("sort", sort.products);
        if (activeType) trendParams.set("product_type_id", activeType);
        const [sts, ss, tr] = await Promise.all([
          api.get(`/streams?${streamParams}`),
          api.get(`/stores?${storeParams}`),
          api.get(`/trending?${trendParams}`),
        ]);
        if (ignore) return;   // category changed while in flight — discard
        setStreams(sts.data || []);
        setStores(ss.data || []);
        setTrending(tr.data?.products || []);
      } catch { /* handled by card empty-states */ }
    })();
    return () => { ignore = true; };
    // sort object identity is derived from sortId — using sortId is enough
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, sortId, activeType]);

  // For-you rail — only fetch for authenticated buyers
  useEffect(() => {
    if (!user) { setForYou([]); setIsPersonalized(false); return; }
    let ignore = false;
    (async () => {
      try {
        const { data } = await api.get(`/buyer/recommended`,
          { params: { category_id: id, limit: 8 } });
        if (ignore) return;
        setForYou(data.products || []);
        setIsPersonalized(!!data.is_personalized);
      } catch { if (!ignore) { setForYou([]); setIsPersonalized(false); } }
    })();
    return () => { ignore = true; };
  }, [user, id]);

  // "Rent the look" rail — only fires when the current category is
  // flagged rentals_eligible.
  const cat = useMemo(() => cats.find(c => c.category_id === id), [cats, id]);
  const catIsRentable = useMemo(
    () => cats.find(c => c.category_id === id)?.rentals_eligible === true,
    [cats, id]
  );
  useEffect(() => {
    if (!id || !catIsRentable) { setRentals([]); return; }
    let ignore = false;
    (async () => {
      try {
        const { data } = await api.get("/rentals/search",
          { params: { category_id: id, limit: 8, sort: "rating" } });
        if (!ignore) setRentals(data?.items || []);
      } catch { if (!ignore) setRentals([]); }
    })();
    return () => { ignore = true; };
  }, [id, catIsRentable]);

  // Preload all-saved once, so the Saved-tab badge shows the correct
  // count without waiting for the user to click through.
  useEffect(() => {
    if (!user) { setSavedAll([]); setSavedLoaded(false); return; }
    api.get("/me/saved/product").then(({ data }) => {
      setSavedAll(data.items || []);
      setSavedLoaded(true);
    }).catch(() => setSavedLoaded(true));
  }, [user]);

  // Filtered product grid refreshes whenever filters, type, sort, or newOnly change
  useEffect(() => {
    let ignore = false;
    const params = new URLSearchParams({ category_id: id, limit: "30" });
    if (sort.products) params.set("sort", sort.products);
    if (activeType) params.set("product_type_id", activeType);
    if (newOnly) params.set("is_new_launch", "true");
    Object.entries(filters).forEach(([k, v]) => {
      if (v != null && v !== "" && v !== false) {
        if (Array.isArray(v)) v.forEach((x) => params.append(k, x));
        else params.set(k, String(v));
      }
    });
    api.get(`/products?${params}`)
      .then(({ data }) => { if (!ignore) setProducts(data || []); })
      .catch(() => { if (!ignore) setProducts([]); });
    return () => { ignore = true; };
    // sort object identity is derived from sortId — using sortId is enough
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, filters, sortId, activeType, newOnly]);

  // Saved-in-this-category (filtered client-side so the Browse tab can
  // also show a "you saved …" hint without a second round-trip).
  const savedInCategory = useMemo(
    () => savedAll.filter(p => p.category_id === id),
    [savedAll, id]
  );

  // Preferences loader — invoked by the parent when the Prefs tab opens.
  const loadProfileAndSchema = async () => {
    if (!user) return;
    try {
      const [p, s] = await Promise.all([
        profile ? Promise.resolve({ data: profile }) : api.get("/buyer/profile"),
        prefSchema ? Promise.resolve({ data: prefSchema }) : api.get("/buyer/preference-schema"),
      ]);
      setProfile(p.data);
      setPrefSchema(s.data);
    } catch (e) { setPrefMsg(formatApiError(e?.response?.data?.detail) || "Failed to load"); }
  };

  // Saved-tab loader — same pattern.
  const loadSavedIfNeeded = () => {
    if (!user || savedLoaded) return;
    api.get("/me/saved/product").then(({ data }) => {
      setSavedAll(data.items || []);
      setSavedLoaded(true);
    }).catch(() => setSavedLoaded(true));
  };

  return {
    // read-only state
    cats, productTypes, products, stores, streams, trending, forYou,
    isPersonalized, rentals, savedInCategory, savedLoaded, profile,
    prefSchema, prefMsg, cat, catIsRentable,
    // reactive picks
    filters, activeType, sortId, newOnly,
    // setters
    setFilters, setActiveType, setSortId, setNewOnly, setProfile, setPrefMsg,
    // helpers
    loadProfileAndSchema, loadSavedIfNeeded,
  };
}
